import { useEffect, useState, useCallback } from "react";
import { supabase } from "./supabaseClient";
import type { RealtimeChannel } from "@supabase/supabase-js";

export interface Invoice {
  id: string;
  case_id: string;
  file_url: string;
  invoice_type: "deposit" | "final";
  status: "draft" | "sent" | "paid" | "unpaid";
  amount: number | null;
  sent_at: string | null;
  paid_at: string | null;
  sent_by: string | null;
  reminder_sent: boolean;
  upload_group_id: string;
  group_position: number;
  version: number;
  created_at: string;
  display_name: string | null;
  archived_at: string | null;
  archived_by: string | null;
}

export interface InvoiceGroup {
  groupId: string;
  version: number;
  invoices: Invoice[];
  createdAt: string;
  amount: number | null;
}

export function useRealtimeInvoices(caseId: string, invoiceType?: "deposit" | "final") {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
      let query = supabase
        .from("invoices")
        .select("*")
        .eq("case_id", caseId)
        .order("created_at", { ascending: false });

      // Filtrer par type si spécifié
      if (invoiceType) {
        query = query.eq("invoice_type", invoiceType);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Erreur lors du chargement des factures:", error);
      } else {
        setInvoices((data as Invoice[]) || []);
      }

      setLoading(false);
  }, [caseId, invoiceType]);

  useEffect(() => {
    let channel: RealtimeChannel;
    refresh();

    // S'abonner aux changements en temps réel
    // Utiliser un nom de canal unique incluant le type pour éviter les conflits
    const channelName = invoiceType 
      ? `invoices:${caseId}:${invoiceType}` 
      : `invoices:${caseId}:all`;
    
    channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "invoices",
          filter: `case_id=eq.${caseId}`,
        },
        (payload) => {
          if (payload.eventType === "INSERT") {
            const newInvoice = payload.new as Invoice;
            // Filtrer par type si nécessaire
            if (!invoiceType || newInvoice.invoice_type === invoiceType) {
              setInvoices((prev) => [newInvoice, ...prev]);
            }
          } else if (payload.eventType === "UPDATE") {
            const updatedInvoice = payload.new as Invoice;
            if (!invoiceType || updatedInvoice.invoice_type === invoiceType) {
              setInvoices((prev) =>
                prev.map((inv) => (inv.id === updatedInvoice.id ? updatedInvoice : inv))
              );
            }
          } else if (payload.eventType === "DELETE") {
            setInvoices((prev) => prev.filter((inv) => inv.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [caseId, invoiceType, refresh]);

  // Grouper les factures par upload_group_id
  const groupInvoices = (items: Invoice[]): InvoiceGroup[] => items.reduce((groups, invoice) => {
    const existingGroup = groups.find((g) => g.groupId === invoice.upload_group_id);

    if (existingGroup) {
      existingGroup.invoices.push(invoice);
      // Trier par position dans le groupe
      existingGroup.invoices.sort((a, b) => a.group_position - b.group_position);
    } else {
      groups.push({
        groupId: invoice.upload_group_id,
        version: invoice.version,
        invoices: [invoice],
        createdAt: invoice.created_at,
        amount: invoice.amount,
      });
    }

    return groups;
  }, [] as InvoiceGroup[]);

  const activeInvoices = invoices.filter((invoice) => !invoice.archived_at);
  const archivedInvoices = invoices.filter((invoice) => !!invoice.archived_at);
  const groupedInvoices = groupInvoices(activeInvoices);
  const archivedGroups = groupInvoices(archivedInvoices);
  groupedInvoices.sort((a, b) => b.version - a.version || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return {
    invoices: activeInvoices,
    archivedGroups,
    groupedInvoices,
    refresh,
    loading,
  };
}
